import { Form, Formik } from "formik";
import { useMutation } from "@apollo/client";
import { GroupBase } from "react-select";
import { useMemo } from "react";

import {
  Button,
  Card,
  FieldWrapper,
  Input,
  Text,
  SelectField,
  Spacing,
  Tipbox,
} from "@otta/design";
import {
  BrandAssetGroup,
  CompanyBrandAssetLinkInput,
  CompanyBrandAssetsQuery,
  CreateCompanyBrandAssetLinkDocument,
  Region,
  UpdateCompanyBrandAssetLinkDocument,
} from "@toolbox/schema";

type Company = NonNullable<CompanyBrandAssetsQuery["company"]>;
type Asset = Company["brandAssets"][0];
type Link = Asset["links"][0];

const linkKeys: readonly (keyof CompanyBrandAssetLinkInput)[] = [
  "brandAssetGroupName",
  "companyTeamId",
  "jobId",
  "regionId",
];

const nullLinks: CompanyBrandAssetLinkInput = Object.fromEntries(
  linkKeys.map(l => [l, null])
);

function areEqual(
  a: CompanyBrandAssetLinkInput,
  b: CompanyBrandAssetLinkInput
) {
  for (const key of linkKeys) {
    if (b[key] !== a[key]) {
      return false;
    }
  }
  return true;
}

function LinkSelect({
  onChange,
  company,
  value,
  regions,
}: {
  company: Company;
  value: CompanyBrandAssetLinkInput;
  onChange(set: CompanyBrandAssetLinkInput): void;
  regions: Region[];
}): React.ReactElement {
  const options: GroupBase<{
    value: CompanyBrandAssetLinkInput;
    label: string;
  }>[] = useMemo(() => {
    return [
      {
        label: "Groups",
        options: Object.values(BrandAssetGroup).map(l => ({
          value: { brandAssetGroupName: l },
          label: l,
        })),
      },
      {
        label: "Teams",
        options: company.teams.map(t => ({
          value: { companyTeamId: t.id },
          label: t.name,
        })),
      },
      {
        label: "Jobs",
        options: company.liveJobs.map(t => ({
          value: { jobId: t.id },
          label: t.title,
        })),
      },
      {
        label: "Regions",
        options: regions.map(r => ({
          value: { regionId: r.id },
          label: r.displayName,
        })),
      },
    ];
  }, [company]);

  const val = useMemo(() => {
    for (const { options: opts } of options) {
      for (const optionValue of opts) {
        if (areEqual(value, optionValue.value)) {
          return optionValue;
        }
      }
    }
  }, [options, value]);

  return (
    <SelectField
      options={options}
      onChange={v => onChange(v?.value ?? {})}
      value={val}
    />
  );
}

export function LinkForm({
  company,
  parent,
  regions,
}: {
  company: Company;
  parent: Asset | Link;
  regions: Region[];
}): React.ReactElement {
  const updating = parent.__typename === "CompanyBrandAssetLink";

  const [run, { error }] = useMutation(
    updating
      ? UpdateCompanyBrandAssetLinkDocument
      : CreateCompanyBrandAssetLinkDocument
  );

  const initialValues: CompanyBrandAssetLinkInput = useMemo(() => {
    if (updating) {
      return {
        jobId: parent.job?.id,
        options: parent.options,
        priority: parent.priority,
        brandAssetGroupName: parent.brandAssetGroup?.name,
        companyTeamId: parent.companyTeam?.id,
        regionId: parent.region?.id,
      };
    } else {
      return {
        options: undefined,
        priority: undefined,
      };
    }
  }, [parent, updating]);

  return (
    <Card>
      <Spacing size={1}>
        <Text bold size={1}>
          {updating ? `Link #${parent.id}` : "Add link"}
        </Text>
        {!!error && <Tipbox level="error">{error.message}</Tipbox>}
        <Formik<CompanyBrandAssetLinkInput>
          onSubmit={input => run({ variables: { id: parent.id, input } })}
          initialValues={initialValues}
        >
          {({ values, setValues, handleChange, isSubmitting }) => (
            <Form>
              <Spacing>
                <LinkSelect
                  value={values}
                  company={company}
                  onChange={value =>
                    setValues({ ...values, ...nullLinks, ...value })
                  }
                  regions={regions}
                />
                <FieldWrapper required label="priority">
                  {() => (
                    <Input
                      type="number"
                      name="priority"
                      value={values.priority ?? undefined}
                      onChange={handleChange}
                    />
                  )}
                </FieldWrapper>
                <FieldWrapper label="imgproxy options">
                  {() => (
                    <Input
                      type="text"
                      name="options"
                      value={values.options ?? undefined}
                      onChange={handleChange}
                    />
                  )}
                </FieldWrapper>
                <Button level="primary" type="submit" disabled={isSubmitting}>
                  {updating ? "Save changes" : "Add link"}
                </Button>
              </Spacing>
            </Form>
          )}
        </Formik>
      </Spacing>
    </Card>
  );
}
